// Element creation without a framework. `el` assigns properties straight onto
// the node, so you write native names — className, onclick, textContent — and
// TypeScript checks them against the real element type.

export type DomChild = Node | string | number | boolean | null | undefined | readonly DomChild[];

export type ElementProps<K extends keyof HTMLElementTagNameMap> = Partial<
  Omit<HTMLElementTagNameMap[K], 'style' | 'dataset' | 'classList' | 'children'>
> & {
  /** Inline style as a CSS string. Prefer a class; use this for one-off custom properties. */
  style?: string;
  dataset?: Record<string, string>;
};

export const el = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props?: ElementProps<K>,
  children?: readonly DomChild[],
): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);

  if (props) {
    const { style, dataset, ...rest } = props;
    Object.assign(node, rest);
    if (style) node.setAttribute('style', style);
    if (dataset) Object.assign(node.dataset, dataset);
  }

  if (children) append(node, children);
  return node;
};

export const append = (parent: Node, children: readonly DomChild[]): void => {
  for (const child of children) {
    if (child === null || child === undefined || typeof child === 'boolean') continue;
    if (child instanceof Node) parent.appendChild(child);
    else if (Array.isArray(child)) append(parent, child);
    else parent.appendChild(document.createTextNode(String(child)));
  }
};

/** Replaces everything under `parent`. Accepts a node, a string, or an array. */
export const render = (parent: Element, children: DomChild): void => {
  parent.replaceChildren();
  append(parent, [children]);
};

/** Throws instead of returning null, so a typo in a selector fails loudly. */
export const requireElement = <T extends Element = HTMLElement>(
  selector: string,
  within: ParentNode = document,
): T => {
  const node = within.querySelector<T>(selector);
  if (node === null) throw new Error(`No element matches "${selector}"`);
  return node;
};
